import { Box, Button, Flex, Skeleton, Text } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTabs } from 'components/common/tabs';
import {
  InstallationContext,
  PerformanceStepTabKey,
} from 'components/machine/dialogs/installation/context';
import { CommonStepHeader } from 'components/machine/dialogs/installation/header';
import { DataCollectorHoC } from 'components/machine/dialogs/installation/steps/data-collector';
import { MachineContext } from 'contexts/machine.context';
import { SubStatus } from 'enums/installation';
import { t } from 'i18next';
import { InstallStep } from 'lib_ts/enums/installation';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ICollectionShotGroup } from 'lib_ts/interfaces/training/i-machine-shot';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { PitchListsService } from 'services/pitch-lists.service';
import useMiscStore from 'stores/use-misc-store';
import { RepeatabilityResults } from './results';

interface IProps {
  state: SubStatus;
  onStart: () => void;
  onComplete: () => void;
  updateFiringLog?: (agg: ICollectionShotGroup[]) => void;
}

export const StepRepeatability = (props: IProps) => {
  const {
    installation,
    rt,
    rtTrainingPitches,
    rtTab,
    setRtTab,
    trainRtPitches,
    reloadRT,
  } = useContext(InstallationContext);
  const { machine } = useContext(MachineContext);
  const [listName, setListName] = useState<string>();

  const [loading, setLoading] = useState(false);

  // after the first time, it will not force you to go back
  const [goToResults, setGoToResults] = useState(true);

  // list name for subheading
  useEffect(() => {
    if (!rt?.listID) {
      return;
    }

    const callback = async () => {
      try {
        setLoading(true);

        const list = await PitchListsService.getInstance().getOne(rt.listID);
        if (!list) {
          throw new Error(
            `Failed to find list with ID ${rt.listID} for data collection`
          );
        }

        setListName(list?.name);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    callback();
  }, [rt?.listID]);

  return (
    <ErrorBoundary identifier="StepRepeatability">
      <Flex
        direction="column"
        gap="4"
        style={{
          height: '100%',
          // border: '1px solid red'
        }}
      >
        {installation.standalone !== InstallStep.Repeatability && (
          <Flex direction="column" gap="1">
            <CommonStepHeader label="common.repeatability-test" />
            {listName && (
              <Text color={RADIX.COLOR.HINT} size={RADIX.TEXT.SIZE.HINT}>
                {t('common.pitch-list')}: {listName}
              </Text>
            )}
          </Flex>
        )}

        {props.state === SubStatus.NotStarted && (
          <Flex
            flexGrow="1"
            direction="column"
            gap="5"
            align="center"
            justify="center"
            style={
              {
                // border: '1px solid blue'
              }
            }
          >
            <Box>
              <img
                style={{
                  width: '176px',
                }}
                src={useMiscStore
                  .getState()
                  .getThemeImgPath('/renders/repeatability.svg')}
              />
            </Box>

            <Text align="center" color={RADIX.COLOR.SECONDARY}>
              {t('admin.repeatability-description')}{' '}
              <Trans
                i18nKey="admin.only-x-ball-type"
                t={t}
                values={{
                  x: machine.ball_type,
                }}
                components={[<strong key="0" />]}
              />
            </Text>

            <Flex gap="2" align="center" justify="center">
              {rtTrainingPitches && rtTrainingPitches.length > 0 && (
                <Button
                  color={RADIX.COLOR.WARNING}
                  onClick={() => trainRtPitches()}
                >
                  Train {rtTrainingPitches.length} Pitches
                </Button>
              )}
              <Button onClick={props.onStart}>
                Start Repeatability Testing
              </Button>
            </Flex>
          </Flex>
        )}

        {props.state !== SubStatus.NotStarted && (
          <Skeleton loading={loading}>
            <Box flexGrow="1">
              <CommonTabs
                tabs={[
                  {
                    label: 'Data Collection',
                    value: PerformanceStepTabKey.DataCollection,
                    content: rt ? (
                      <DataCollectorHoC
                        identifier="RtDataCollector"
                        // for drawing error instead of complete
                        state={props.state}
                        id={rt._id}
                        onComplete={() => {
                          props.onComplete();
                          if (goToResults) {
                            setGoToResults(false);
                            setRtTab(PerformanceStepTabKey.Results);
                          }
                        }}
                        updateFiringLog={props.updateFiringLog}
                        // update the RT collection value in the InstallationContext when it's changed in the data collector
                        onToggleLowConfidence={reloadRT}
                      />
                    ) : (
                      <CommonCallout text="Cannot proceed without a valid data collection." />
                    ),
                  },
                  {
                    label: 'Results',
                    value: PerformanceStepTabKey.Results,
                    content: (
                      <RepeatabilityResults machineID={machine.machineID} />
                    ),
                  },
                ]}
                value={rtTab}
                onValueChange={(value) =>
                  setRtTab(value as PerformanceStepTabKey)
                }
              />
            </Box>
          </Skeleton>
        )}
      </Flex>
    </ErrorBoundary>
  );
};
